import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { usePost, get_deployment } from "../API";
import * as Sentry from "@sentry/react";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { initializeRTC } from "./RTCConnection";
import { RealtimeClientEvent, RealtimeServerEvent, SessionUpdateEvent, ConversationItem, SessionCreatedEvent, ConversationItemWithReference, ResponseAudioTranscriptDoneEvent, ResponseTextDoneEvent, ConversationItemCreateEvent, ErrorEvent } from 'openai/resources/beta/realtime/realtime';
import { safeParse } from "../Util";
import zod from 'zod';
import { zodResponseFormat } from 'openai/helpers/zod';
import { AIConversation, ConversationItemWithAKMeta } from "@aidkitorg/types/lib/convoai/conversation";

export type Tool<T extends zod.ZodObject<any>, Out> = {
  name: string,
  description: string,
  schema: T,
  fn: (args: zod.infer<T>) => Promise<Out>
};

export type ControlSignal = { type: 'clear-chat' } | { type: 'update-item-meta', id: string, update: Partial<ConversationItemWithAKMeta["meta"]> };
export type ChatEvent = RealtimeServerEvent | RealtimeClientEvent | ControlSignal;

// conversations are stored as JSON, Dates are just strings
type StoredConversation = Omit<AIConversation, 'created_at' | 'updated_at'> & { created_at: string, updated_at: string }
const sessionStorageKey = "ai_coach_conversation";
/**
 * Low-level hook that allows working directly with the events send to/from OpenAI Realtime using WebRTC.
 *
 * Only use this if you want to create an entire new mode of interaction. Otherwise, use `useChatChannel()`.
 */
export function useChatEvents<S>(reducer: Reducer<S, ChatEvent>, initial: S, sessionType?: 'survey' | 'distro', persistConversation?: boolean) {
  const tokenEndpoint = sessionType === 'distro' ? '/program/admin/ai_chat/create' : '/coach/conversation';
  const createConversation = usePost(tokenEndpoint);
  const [channel, setChannel] = useState<RTCDataChannel | null>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [sender, setSender] = useState<RTCRtpSender | null>(null);
  const [channelState, setChannelState] = useState<{ status: 'closed' | 'open' | 'initializing' } | { status: 'error', reason: string }>({ status: 'closed' });
  const [state, dispatch] = useReducer(reducer, initial);
  const [enabled, setEnabled] = useState(false);
  const [realtimeSession, setRealtimeSession] = useState<SessionCreatedEvent["session"] | null>(null);
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);
  const [conversation, setConversation] = useState<StoredConversation>();

  useAsyncEffect(async (cancel) => {
    if (!enabled) {
      return;
    }

    const session = await createConversation({
      modality: 'text',
      persistConversation,
      previousConvoId: safeParse(sessionStorage.getItem(sessionStorageKey)!, null)?.id
    }, undefined, cancel);

    const token = session.token;

    if (!token) {
      return;
    }

    if ("conversation" in session && session.conversation) {
      setConversation({
        ...session.conversation,
        items: session.conversation?.items ?? []
      });
    }

    setChannelState({ status: 'initializing' });

    const { dataChannel, pc, sender } = await initializeRTC(token, {
      onReady() {
        setChannel(dataChannel);
        setSender(sender);
        setPeerConnection(pc);
        setChannelState({ status: 'open' });
      },
      onTrack(e) {
        if (e.streams[0]) {
          setOutputStream(e.streams[0]);
        }
      },
      onConnectionError(e) {
        setChannelState({ status: 'error', reason: e });
        setSender(null)
        setPeerConnection(null);
        setChannel(null);
      },
      onClose() {
        setChannelState({ status: 'closed' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
      },
      onError() {
        setChannelState({ status: 'error', reason: 'unknown' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
      }
    });

    cancel.addEventListener('abort', () => {
      dataChannel.close();
      setChannelState({ status: 'closed' })
    }, { once: true, passive: true });

  }, [enabled]);

  useEffect(() => {

    const messageHandler = (msg: MessageEvent) => {
      const event = safeParse(msg.data) as RealtimeServerEvent | null;
      if (event) {
        switch (event?.type) {
          case "session.created":
          case "session.updated":
            setRealtimeSession(event.session);
            break;
          default:
            dispatch(event);
        }
      }
    };

    if (channel) {
      channel.addEventListener('message', messageHandler);
    }

    return () => channel?.removeEventListener('message', messageHandler);
  }, [channel]);

  const channelIsReady = useMemo(() => {
    return channel && channelState.status === "open" && channel.readyState === "open";
  }, [channel, channelState]);

  const sendEvent = useCallback((message: ChatEvent) => {
    if (!channelIsReady) {
      console.warn("[AI COACH] failed to send event - channel is not ready");
      return;
    }

    dispatch(message);
    channel?.send(JSON.stringify(message));

  }, [dispatch, channel, channelIsReady]);

  const manageAudioSubscription = useCallback((action: 'subscribe' | 'unsubscribe', fn: (event: RTCTrackEvent) => void) => {
    if (action === 'subscribe') {
      peerConnection?.addEventListener('track', fn);
    } else {
      peerConnection?.removeEventListener('track', fn);
    }
  }, []);

  return { manageAudioSubscription, channelIsReady, setEnabled, enabled, sendEvent, dispatch, state, status: channelState, realtimeSession, sender, setSender, outputStream, conversation };
}

/**
 * Sometimes OpenAI Realtime responds with json/pseudo JSON, and sometimes not...
 * This attempts to parse it if it's JSON, and it cleans up non-meaningful characters
 * @param s
 */
function chatContentToText(s: string) {
  const json = safeParse(s, null);
  return (json?.message ?? json?.response ?? s).replaceAll(/^(\{|\")+|(\"|\})+$/g, "");
}

/**
 * Wraps the Chat Events from WebRTC for a more structured hook.
 * Some methods, such as Enabling and Monitoring the Connection and Audio Subscription
 * are passed through to the consumer of the hook.
 *
 * Provides methods to Send and Manage Messages.
 */
export function useChatChannel(sessionType: 'survey' | 'distro' = 'survey') {
  const updateConversation = usePost('/coach/conversation/update', { keepAlive: true });
  const tools = useRef<Record<string, Tool<zod.ZodObject<any>, any>>>({});
  const pendingMeta = useRef<Map<string, ConversationItemWithAKMeta["meta"]>>(new Map());
  const chat = useChatEvents(({ map, list, toolCalls, error, phase }, action) => {

    function updateItemMeta(itemId: string | undefined, update: Partial<ConversationItemWithAKMeta["meta"]>) {
      const itemToUpdate = itemId ? map.get(itemId) : undefined;
      if (itemToUpdate) {
        itemToUpdate.meta = {
          ...itemToUpdate.meta,
          ...update
        };
      }
    }
    
    function incrementVersion(itemId: string | undefined) { 
      const itemToUpdate = itemId ? map.get(itemId) : undefined;
      if (itemToUpdate) {
        itemToUpdate.meta = {
          ...itemToUpdate.meta,
          version: (itemToUpdate.meta.version ?? 0) + 1
        };
      }      
    }

    let passThrough = false;

    switch (action.type) {
      /**
       * CUSTOM EVENTS
       */
      case "clear-chat":
        return {
          map: new Map(),
          list: [],
          toolCalls: new Set<string>(),
          error: null,
          phase: null
        };

      case "update-item-meta":
        updateItemMeta(action.id, action.update);
        break;

      /**
       * CLIENT EVENTS
       */
      case "conversation.item.create":
        if (action.item.type === 'function_call_output') {
          if (!action.previous_item_id) {
            console.error("no previous item id specified, tool call couldn't be removed");
          } else if (!toolCalls.delete(action.previous_item_id)) {
            console.error("tool call not found using id", action.previous_item_id);
          }
        }
        action.item.id ??= action.item.id ?? action.item.call_id ?? uniqueOrderedId(map);

        map.set(action.item.id, {
          ...action.item,
          // role is only applicable for message types
          role: action.item.type === "message" ? action.item.role ?? 'user' : undefined,
          meta: pendingMeta.current?.get(action.item.id) ?? {
            time: Date.now(),
            version: 0,
            hidden: action.item.type !== 'message' || action.item.role === 'system'
          }
        });
        break;
      /**
       * SERVER EVENTS
       */
      case "input_audio_buffer.speech_started":
        phase = 'listening';
        if (action.item_id && !map.has(action.item_id)) {
          map.set(action.item_id, {
            id: action.item_id,
            type: "message",
            role: "user",
            meta: {
              time: Date.now(),
              version: 0,
              phase: "in_process"
            }
          });
        }
        break;
      case "input_audio_buffer.speech_stopped":
        phase = 'thinking';
        updateItemMeta(action.item_id, {
          phase: "pending"
        })
        break;
      case "conversation.item.created":
        // remote has created a new conversation item.
        // this typically comes in the form of an audio input or output
        // item being added to the transcript
        const metaUpdate = (action.item.id ? pendingMeta.current?.get(action.item.id) : null) ?? {
          time: Date.now(),
          version: 0,
          hidden: action.item.type !== 'message' || action.item.role === 'system'
        };
        if (action.item.id) {
          // upsert conversation item
          if (!map.has(action.item.id)) {
            map.set(action.item.id, {
              ...action.item,
              meta: metaUpdate
            })
          }
          else {
            updateItemMeta(action.item.id, metaUpdate);
          }
        }
        break;
      case "response.output_item.added":
        phase = 'speaking';
        // remote added a new response
        if (action.item.id && !map.has(action.item.id)) {
          map.set(action.item.id, {
            ...action.item,
            meta: {
              time: Date.now(),
              version: 0,
              hidden: !!action.item.call_id
            }
          });
        }
        break;
      case "conversation.item.input_audio_transcription.delta":
      case "response.audio_transcript.delta":
      case "response.text.delta":
        // appending new text onto an item
        const itemToUpdateWithDelta = action.item_id ? map.get(action.item_id) : undefined;
        if (itemToUpdateWithDelta && action.delta) {
          const content = itemToUpdateWithDelta.content = itemToUpdateWithDelta.content ?? [];
          const index = typeof action.content_index === "number" ? action.content_index : 0;
          content[index] = content[index] ?? {};
          // sometimes openAI sends deltas that look like:
          // {"message": "here is the actual message"}
          // this attempts to fix those as they come in
          content[index].text = [content[index].text ?? "", action.delta.replace(/^(\{|\")+|(\"|\})+$/g, "")].join("");
          content[index].type = action.type === "conversation.item.input_audio_transcription.delta" ? "input_audio" : "text";
          incrementVersion(action.item_id)
        }
        break;
      case "response.function_call_arguments.done":
        const id = action.item_id;
        const existingItem = map.get(id);
        map.set(id, {
          type: 'function_call',
          name: existingItem!.name,
          call_id: action.call_id,
          arguments: action.arguments,
          meta: {
            time: Date.now(),
            hidden: true
          }
        });
        toolCalls.add(id);
        break;
      case "response.output_item.done":
        // remote has completed the response
        updateItemMeta(action.item?.id, { phase: "done" });
        break;
      case "conversation.item.input_audio_transcription.completed":
        updateItemMeta(action.item_id, { phase: "done" });
        incrementVersion(action.item_id);
        break;
      case "response.done":
        phase = 'listening';
        break;
      case "response.text.done":
      case "response.audio_transcript.done":
        phase = 'listening';
        let completedText = (action as ResponseTextDoneEvent).text;
        let completedTranscript = (action as ResponseAudioTranscriptDoneEvent).transcript;

        const itemToCompleteTranscript = action.item_id ? map.get(action.item_id) : undefined;
        if (itemToCompleteTranscript) {
          itemToCompleteTranscript.status = "completed";
          const content = itemToCompleteTranscript.content = itemToCompleteTranscript.content ?? [];
          const index = typeof action.content_index === "number" ? action.content_index : 0;
          content[index] = content[index] ?? {};
          content[index].text = chatContentToText(completedText ?? completedTranscript) ?? content[index].text;
          incrementVersion(action.item_id);
        }
        break;
      case "error":
        phase = 'listening';
        passThrough = true;
        console.error(action);
        error = action.error;
        const deploymentName = get_deployment();
        Sentry.setContext('deployment', { 'name': deploymentName });
        Sentry.setTag("deployment_name", deploymentName);
        Sentry.captureException(error);
        break;
      default:
        passThrough = true;
        // console.debug('[AI CHAT] unhandled event:', action.type);
        break;
    }

    return passThrough
      ? { map, list, toolCalls, error, phase }
      : { map: new Map(map), list: Array.from(map.values()), toolCalls: new Set(toolCalls), error, phase };
  }, { map: new Map(), list: [], toolCalls: new Set<string>, error: null, phase: null } as {
    map: Map<string, ConversationItemWithAKMeta>,
    list: (ConversationItemWithAKMeta)[],
    toolCalls: Set<string>,
    error: ErrorEvent["error"] | null,
    phase: null | 'listening' | 'thinking' | 'speaking'
  }, sessionType, true);

  const updateSession = useCallback((session: SessionUpdateEvent['session']) => {
    chat.sendEvent({
      type: 'session.update',
      session
    });
  }, [chat.sendEvent]);

  const sendChatMessage = useCallback((text: string, opts?: { changeMessageId?: string, createResponse?: boolean, allowAudio?: boolean, role?: 'user' | 'system' }) => {
    const now = Date.now();
    const id = opts?.changeMessageId || uniqueOrderedId(chat.state.map, now);
    const conversationItem: ConversationItem = {
      id,
      type: "message",
      role: opts?.role ?? 'user',
      content: [
        {
          type: "input_text",
          text
        }
      ]
    };

    // also send message to remote
    chat.sendEvent({
      type: "conversation.item.create",
      item: conversationItem
    });

    if (opts?.createResponse) {
      chat.sendEvent({
        type: "response.create",
        response: {
          modalities: opts?.allowAudio ? ['text', 'audio'] : ['text']
        }
      });
    }

    return { messageId: id };
  }, [chat.sendEvent]);

  const restart = useCallback(() => {
    chat.setEnabled(false);
    chat.sendEvent({ type: 'clear-chat' });
    setTimeout(() => chat.setEnabled(true));
  }, [chat.sendEvent, chat.setEnabled]);

  const registerTool = useCallback(<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>) => {

    const newTools = { ...tools.current, [tool.name]: tool as unknown as Tool<zod.ZodObject<any>, any> };

    tools.current = newTools;

    if (chat.enabled && chat.channelIsReady) {
      chat.sendEvent({
        type: 'session.update',
        session: {
          tools: Object.values(newTools).map(tool => {
            const { schema } = zodResponseFormat(tool.schema, 'schema').json_schema;
            return {
              type: 'function',
              parameters: schema,
              name: tool.name,
              description: tool.description
            }
          })
        }
      });
    }
  }, [chat.enabled, chat.channelIsReady, chat.sendEvent, tools]);

  useAsyncEffect(async () => {
    let shouldCreateResponse = false;
    for (const id of Array.from(chat.state.toolCalls)) {
      const message = chat.state.map.get(id);

      if (!message) {
        console.error('[AI CHAT] no message found for function call to reference.');
        continue;
      } else if (message?.type !== 'function_call') {
        console.error('[AI CHAT] somehow a non-function-call id got mapped to a function call arg');
        continue;
      } else if (!message.name) {
        console.error('[AI CHAT] no tool name specified in original message');
        continue;
      }

      const tool = tools.current[message.name];

      if (!tool) {
        console.error('no tool found with the name', message.name);
        continue;
      }

      if (!message.arguments) {
        console.error('[AI CHAT] no arguments provided for function call', message);
        continue;
      }

      // using a symbol guarantees a unique identifier for a bad outcome
      const badResultSymbol = Symbol.for('BadArgFormat');
      const args = safeParse(message.arguments, badResultSymbol);

      if (args === badResultSymbol) {
        console.error('[AI CHAT] failed to parse arguments from:', message.arguments);
        continue;
      }

      console.log('[AI CHAT] executing tool call', tool.name, 'with arguments', args);

      let result: any;
      try {
        result = await tool.fn(args);
      } catch (err: any) {
        result = { error: err?.message || err };
      }

      console.debug('[AI CHAT] tool call result:', result, JSON.stringify(result));

      chat.sendEvent({
        type: 'conversation.item.create',
        previous_item_id: id,
        item: {
          id: uniqueOrderedId(chat.state.map, Date.now()),
          type: 'function_call_output',
          output: JSON.stringify(result),
          call_id: message.call_id,
        }
      });

      shouldCreateResponse = true;
    }

    if (shouldCreateResponse) {
      chat.sendEvent({
        type: 'response.create',
      });
    }

  }, [chat.state.toolCalls, registerTool]);

  const getStoredConversation: () => StoredConversation | null = useCallback(() => {
    return sessionStorage.getItem(sessionStorageKey) ? safeParse(sessionStorage.getItem(sessionStorageKey)!, null) : null;
  }, []);

  const updateStoredConversation: (update: Partial<StoredConversation>) => void = useCallback((update) => {
    const convo = getStoredConversation();
    if (!convo || !chat.conversation?.id || chat.conversation.id !== convo.id) {
      console.warn("[AI CHAT] Failed to update - no locally stored conversation found");
      return;
    }
    return sessionStorage.setItem(sessionStorageKey, JSON.stringify({
      ...convo,
      ...update
    }));
  }, [getStoredConversation, chat.conversation?.id]);

  // create a new stored conversation
  useEffect(() => {
    if (chat.conversation?.id && chat.channelIsReady && pendingMeta.current) {
      // if previous convo exists, populate this new convo
      // with previous convo items
      const items = chat.conversation.items ?? [];
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const { meta, object, status, ...rest } = item;

        // can't recreate content with audio data, so just convert them to text
        for (const content of item.content ?? []) {
          if (content.type === "input_audio") {
            content.type = "input_text";
          }
        }

        chat.sendEvent({
          type: "conversation.item.create",
          item: {
            ...rest as ConversationItem,
            // only message types can accept role. remove it otherwise
            role: rest.type === "message" ? rest.role : undefined
          },
          previous_item_id: items[i - 1]?.id
        });

        // stored meta can not be passed into the above event
        // so store it in a ref so that it can be added separately
        // when the item gets created
        if (meta && rest.id) {
          pendingMeta.current.set(rest.id, meta);
        }
      }

      return sessionStorage.setItem(sessionStorageKey, JSON.stringify(chat.conversation));
    }
  }, [chat.conversation?.id, getStoredConversation, chat.channelIsReady, pendingMeta.current]);

  // when there is any change to the conversation, update the stored conversation
  useEffect(() => {
    if (chat.enabled && chat.channelIsReady) {
      updateStoredConversation({
        items: chat.state.list
      })
    }
  }, [chat.enabled, chat.channelIsReady, chat.state.list, updateStoredConversation]);

  // when there is any change to the realtime session, update the stored conversation
  useEffect(() => {
    if (chat.realtimeSession) {
      const storedConversation = getStoredConversation();
      updateStoredConversation({
        metadata: {
          ...(storedConversation?.metadata ?? {}),
          session: chat.realtimeSession
        }
      });
    }
  }, [chat.realtimeSession, getStoredConversation])

  const saveConversation = async () => {
    const storedConversation = getStoredConversation();
    if (storedConversation) {
      const { id, metadata, items } = storedConversation;
      await updateConversation({
        id,
        update: {
          metadata,
          items
        }
      });
    }
  };

  return {
    updateSession,
    sendChatMessage,
    restart,
    registerTool,
    saveConversation,
    messages: chat.state.list,
    ...chat
  };
}

export const ChatChannelContext = React.createContext({} as ChatChannel);

function uniqueOrderedId(existing: Map<string, any>, timestamp = Date.now()) {
  // generate a (mostly) collision proof ID that creates a natural sort order.
  // - 1743692588848_000
  // - 1743692588848_001
  // - 1743692588849_000
  let existingItemWithTimestamp;
  let dedupeIndex = 0;
  let id: string | undefined;
  do {
    id = `${timestamp}_${dedupeIndex.toString().padStart(3, '0')}`;
    existingItemWithTimestamp = existing.get(`convo_${id}`);
    dedupeIndex += 1;
  } while (existingItemWithTimestamp);

  return id;
}

export type ChatChannel = ReturnType<typeof useChatChannel>;
